import {useMemo} from 'react'
import {useQueries} from '@tanstack/react-query'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'

const CHUNK_SIZE = 24

/**
 * Fetches products in parallel chunks to work around the SCAPI 24-item limit.
 * Returns the same {data, isLoading} shape as useProducts, where data is an id-keyed map.
 *
 * @param {string} productIds - Comma-separated product IDs
 * @param {Object} queryOptions - React Query options (enabled, staleTime, etc.)
 */
export const useProductsChunked = (productIds, queryOptions = {}) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {enabled = true, staleTime} = queryOptions

    const idChunks = useMemo(() => {
        if (!productIds) return []
        const ids = productIds.split(',').filter(Boolean)
        const chunks = []
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            chunks.push(ids.slice(i, i + CHUNK_SIZE))
        }
        return chunks
    }, [productIds])

    const queries = useQueries({
        queries: idChunks.map((chunk) => ({
            queryKey: ['products', chunk.join(',')],
            queryFn: async () => {
                const accessToken = await getTokenWhenReady()
                return api.shopperProducts.getProducts({
                    parameters: {
                        ids: chunk.join(','),
                        allImages: true,
                        perPricebook: true
                    },
                    headers: accessToken
                        ? {
                              Authorization: `Bearer ${accessToken}`
                          }
                        : undefined
                })
            },
            enabled: enabled && chunk.length > 0,
            ...(staleTime !== undefined && {staleTime})
        }))
    })

    const isLoading = idChunks.length > 0 && queries.some((q) => q.isLoading)

    // Merge all chunks into a single id-keyed map, matching the shape produced
    // by the select reducer in the original useProducts call
    const data = useMemo(() => {
        if (idChunks.length === 0 || queries.some((q) => q.isLoading)) return undefined
        return queries.reduce((acc, query) => {
            const items = query.data?.data ?? []
            items.forEach((item) => {
                acc[item.id] = item
            })
            return acc
        }, {})
    }, [queries, idChunks])

    return {data, isLoading}
}
